<script setup lang="ts">
  import { computed, toRef } from 'vue';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiBadge from '@/components/pos-v2/ui/PosUiBadge.vue';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';
  import V2ReservationStatusBadge from '../V2ReservationStatusBadge.vue';
  import V2ReservationSourceBadge from '../V2ReservationSourceBadge.vue';
  import V2ReservationDepositBadge from '../V2ReservationDepositBadge.vue';
  import { useReservationActions } from '@/composition/reservations/use-reservation-actions';
  import {
    getPrimaryStaffAction,
    isNoShowRisk,
    lateMinutes,
  } from '@/domain/reservations/reservation-status';
  import { formatTime } from '@/domain/reservations/reservation-time';
  import type { IReservation } from '@/interfaces/IReservation';
  const props = defineProps<{
    reservation: IReservation;
    selected?: boolean;
    now: Date;
    timeZone: string;
    toleranceMin: number;
    businessDayStartHour?: number;
  }>();
  const emit = defineEmits<{ (event: 'select', id: number): void }>();
  const { busy, run } = useReservationActions(toRef(props, 'reservation'));
  const primary = computed(() =>
    getPrimaryStaffAction(
      props.reservation,
      props.now,
      props.timeZone,
      props.businessDayStartHour
    )
  );
  const minutesLate = computed(() =>
    lateMinutes(props.reservation, props.now, props.toleranceMin)
  );
  const dimmed = computed(() =>
    ['cancelled', 'no_show'].includes(props.reservation.status)
  );
</script>

<template>
  <div
    class="reservation-row-wrap"
    :class="{ selected, dimmed, actionable: !!primary }"
  >
    <button
      type="button"
      class="reservation-row"
      :aria-pressed="!!selected"
      @click="emit('select', reservation.id)"
    >
      <time class="row-time" :datetime="reservation.startsAt">{{
        formatTime(reservation.startsAt, timeZone)
      }}</time>
      <div class="row-guest">
        <span
          class="guest-name"
          :class="{ cancelled: reservation.status === 'cancelled' }"
          >{{
            reservation.guestName || $t('reservations.common.anonymous')
          }}</span
        >
        <span class="guest-meta">{{
          $tc('reservations.common.people', reservation.partySize, {
            count: reservation.partySize,
          })
        }}</span>
      </div>
      <div class="row-tables">
        <span v-if="reservation.tableNames.length" class="table-chip">{{
          $t('reservations.common.table', {
            name: reservation.tableNames.join(' + '),
          })
        }}</span>
        <span v-else>{{ $t('reservations.common.noTable') }}</span>
      </div>
      <div class="row-badges">
        <PosUiBadge
          v-if="isNoShowRisk(reservation.customer)"
          color="error"
          variant="soft"
          :title="
            $t('reservations.detail.noShowCount', {
              count: reservation.customer?.noShowsCount,
            })
          "
          >{{ $t('reservations.detail.noShowRisk') }}</PosUiBadge
        >
        <PosUiBadge
          v-if="reservation.guestLateMinutes"
          color="warning"
          variant="soft"
        >
          {{
            $t('reservations.detail.runningLate', {
              minutes: reservation.guestLateMinutes,
            })
          }}
        </PosUiBadge>
        <V2ReservationStatusBadge
          :status="reservation.status"
          :late-minutes="minutesLate"
          :auto-closed="reservation.autoClosed"
        />
        <V2ReservationSourceBadge :source="reservation.source" compact />
        <V2ReservationDepositBadge
          :status="reservation.depositStatus"
          :amount="reservation.depositAmount"
        />
        <PosUiIcon
          v-if="reservation.customer && reservation.customer.isClubMember"
          name="star"
          class="club-icon"
          :title="$t('reservations.club.member')"
        />
        <PosUiIcon
          v-if="reservation.notesGuest || reservation.notesInternal"
          name="note-text-outline"
          :title="reservation.notesInternal || reservation.notesGuest"
        />
      </div>
    </button>
    <PosUiButton
      v-if="primary"
      class="quick-action"
      color="neutral"
      variant="outline"
      :loading="busy"
      @click="run(primary.id)"
      >{{ $t(primary.labelKey) }}</PosUiButton
    >
  </div>
</template>

<style scoped lang="scss">
  .reservation-row-wrap {
    @apply tw-relative tw-border-0 tw-border-b tw-border-solid tw-border-neutral-100 tw-bg-white;
    &.selected {
      @apply tw-border-l-4 tw-border-l-orange-600 tw-bg-orange-50;
    }
    &.dimmed {
      @apply tw-opacity-50;
    }
    .reservation-row {
      @apply tw-grid tw-min-h-14 tw-min-w-0 tw-w-full tw-grid-cols-[3.25rem_1fr] tw-items-center tw-gap-x-3 tw-gap-y-2 tw-border-0 tw-bg-transparent tw-px-3 tw-py-3 tw-text-left tw-text-neutral-800;
      &:focus-visible {
        @apply tw-outline-none tw-ring-2 tw-ring-inset tw-ring-orange-500/40;
      }
    }
    .row-time {
      @apply tw-self-start tw-pt-0.5 tw-text-sm tw-font-semibold tw-tabular-nums;
    }
    .row-guest {
      @apply tw-flex tw-min-w-0 tw-flex-col;
    }
    .guest-name {
      @apply tw-truncate tw-text-sm tw-font-semibold;
      &.cancelled {
        @apply tw-line-through;
      }
    }
    .guest-meta {
      @apply tw-text-xs tw-text-neutral-500;
    }
    .row-tables {
      @apply tw-col-start-2 tw-text-xs tw-text-neutral-500;
    }
    .table-chip {
      @apply tw-inline-block tw-rounded tw-bg-neutral-100 tw-px-2 tw-py-1 tw-text-neutral-700;
    }
    .row-badges {
      @apply tw-col-span-2 tw-flex tw-flex-wrap tw-items-center tw-gap-1.5;
    }
    .club-icon {
      @apply tw-text-amber-500;
    }
    .quick-action {
      @apply tw-mr-3 tw-hidden tw-self-center md:tw-inline-flex;
    }
    @screen md {
      .reservation-row {
        @apply tw-grid-cols-[3.25rem_minmax(7rem,1fr)_auto];
      }
      .row-time {
        @apply tw-row-span-2;
      }
      .row-tables {
        @apply tw-col-start-3 tw-row-start-1;
      }
      .row-badges {
        @apply tw-col-start-2 tw-col-end-4;
      }
      &.actionable {
        @apply tw-grid tw-grid-cols-[minmax(0,1fr)_auto];
      }
    }
  }
</style>
